<template>
    <div class="ds-analytics-period">
        <div class="ds-analytics-year">
            <button
                type="button"
                class="ds-schedule-toolbar-icon-button"
                aria-label="Предыдущий год"
                @click="changeYear(-1)"
            >
                <icon width="9" height="13"><IconChevronLeft /></icon>
            </button>
            <span class="ds-analytics-year-value" aria-live="polite">{{ year }}</span>
            <button
                type="button"
                class="ds-schedule-toolbar-icon-button"
                aria-label="Следующий год"
                :disabled="year >= currentYear"
                @click="changeYear(1)"
            >
                <icon width="9" height="13"><IconChevronRight /></icon>
            </button>
        </div>

        <div ref="monthsRef" class="ds-glass-segment ds-analytics-months" role="radiogroup" aria-label="Месяц">
            <button
                v-for="(label, index) in MONTHS"
                :key="label"
                type="button"
                role="radio"
                class="ds-glass-segment-option"
                :class="{ 'is-active': index === month }"
                :aria-checked="index === month"
                :aria-label="`${MONTH_NAMES[index]} ${year}`"
                :disabled="isFuture(year, index)"
                :tabindex="index === month ? 0 : -1"
                @click="goTo(year, index)"
                @keydown="onMonthKeydown($event, index)"
            >
                {{ label }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
// RD-166: период Аналитики — год и лента Янв–Дек над iframe. Будущие месяцы и годы недоступны.
import { computed, nextTick, ref } from 'vue'
import dayjs from 'dayjs'
import IconChevronLeft from '../../ui/icons/IconChevronLeft.vue'
import IconChevronRight from '../../ui/icons/IconChevronRight.vue'
import { useScheduleStore } from '@/stores/scheduleStore'

const store = useScheduleStore()

const MONTHS = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек']
const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь']

const today = dayjs()
const currentYear = today.year()
const currentMonth = today.month()

const monthsRef = ref<HTMLElement | null>(null)
const year = computed(() => store.currentScheduleDate.year())
const month = computed(() => store.currentScheduleDate.month())

function isFuture(targetYear: number, targetMonth: number) {
    return targetYear > currentYear || (targetYear === currentYear && targetMonth > currentMonth)
}

// Повторный выбор активного месяца не меняет контекст и не вызывает новый запрос.
function goTo(targetYear: number, targetMonth: number) {
    if (isFuture(targetYear, targetMonth)) return
    if (targetYear === year.value && targetMonth === month.value) return
    store.goToMonth(targetYear, targetMonth)
}

// Год меняется с сохранением месяца; будущий месяц текущего года заменяется текущим.
function changeYear(delta: number) {
    const targetYear = year.value + delta
    if (targetYear > currentYear) return
    goTo(targetYear, isFuture(targetYear, month.value) ? currentMonth : month.value)
}

function onMonthKeydown(event: KeyboardEvent, index: number) {
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0
    if (!step) return
    event.preventDefault()
    const target = index + step
    if (target < 0 || target > 11 || isFuture(year.value, target)) return
    goTo(year.value, target)
    nextTick(() => monthsRef.value?.querySelectorAll<HTMLButtonElement>('button')[target]?.focus())
}
</script>
